import {
  Gym,
  IMonthlyPulseMediaItem,
  IMonthlyPulseSupportData,
  IOrganisationConfig,
} from 'fitbeat.models';
import { getMembersAnalyticsBranding } from '../analytics/membersAnalyticsBranding';
import { IMonthlyPulseData, toPulseValue } from './MonthlyPulseData';
import { IMonthlyPulseAggregateStats } from './monthlyPulseAggregates';

export interface IBuildMonthlyPulseMeta {
  gym: Gym;
  organisationConfig?: IOrganisationConfig;
  monthLabel: string;
  heading?: string;
}

const DEFAULT_HEADING = 'Pulse Report';
const FITILLION_LOGO_URL = 'icons/fitillion-logo.svg';
const ACTIVITY_GROUP_PLACEHOLDER_IMAGE = 'icons/activity-group-placeholder.png';
const INTEREST_GROUP_PLACEHOLDER_IMAGE = 'icons/group-interests-placeholder.png';

const emptyMedia = (): IMonthlyPulseMediaItem => ({ title: '', subtitle: '', imageUrl: '' });

const withPlaceholderImage = (
  media: IMonthlyPulseMediaItem | null | undefined,
  placeholderImageUrl: string,
): IMonthlyPulseMediaItem => {
  const base = media ?? emptyMedia();
  return {
    ...base,
    imageUrl: base.imageUrl || placeholderImageUrl,
  };
};

/**
 * Combines Monthly Pulse Composer support data with member aggregates into the
 * IMonthlyPulseData document that toMonthlyPulseVariables flattens for rendering.
 * Social stats are derived from XP event counts (congratulationsSent, challengeCompletedMonthly/Weekly,
 * groupPostReacted/Commented) aggregated per member.
 */
export const buildMonthlyPulseData = (
  support: IMonthlyPulseSupportData,
  aggregates: IMonthlyPulseAggregateStats,
  meta: IBuildMonthlyPulseMeta,
): IMonthlyPulseData => {
  const branding = getMembersAnalyticsBranding(meta.organisationConfig);

  const achievementsToDate = toPulseValue(support.achievementsToDate ?? 0);
  const achievementsThisMonth = toPulseValue(support.achievementsThisMonth ?? 0);
  const featured = support.leaderboards.featured;
  // Hidden unless the backend provided a join count (set only for team boards
  // with resolvable scores); panel type alone is unreliable — e.g. league
  // champions over individual boards or failed team queries keep the type but
  // have no count.
  const featuredChallengeJoinVisible = featured.joinerIds !== undefined;

  return {
    meta: {
      heading: meta.heading ?? DEFAULT_HEADING,
      selectedMonth: meta.monthLabel,
      companyName: meta.gym.displayName || meta.gym.name,
      companyLogoUrl: branding.logoUrl ?? null,
      fitillionLogoUrl: FITILLION_LOGO_URL,
      generatedAt: support.generatedAt,
    },
    social: {
      congratulationsSent: {
        toDate: aggregates.congratulationsSentYtd,
        toDateSuffix: aggregates.congratulationsSentYtdSuffix,
        thisMonth: aggregates.congratulationsSentMonth,
        thisMonthSuffix: aggregates.congratulationsSentMonthSuffix,
      },
      challengesCompleted: {
        toDate: aggregates.challengesCompletedYtd,
        toDateSuffix: aggregates.challengesCompletedYtdSuffix,
        thisMonth: aggregates.challengesCompletedMonth,
        thisMonthSuffix: aggregates.challengesCompletedMonthSuffix,
      },
      interactionsCount: {
        toDate: aggregates.interactionsCountYtd,
        toDateSuffix: aggregates.interactionsCountYtdSuffix,
        thisMonth: aggregates.interactionsCountMonth,
        thisMonthSuffix: aggregates.interactionsCountMonthSuffix,
      },
      activityGroupMedia: withPlaceholderImage(
        support.content.activityGroupMedia,
        ACTIVITY_GROUP_PLACEHOLDER_IMAGE,
      ),
      interestGroupMedia: withPlaceholderImage(
        support.content.interestGroupMedia,
        INTEREST_GROUP_PLACEHOLDER_IMAGE,
      ),
    },
    wellness: {
      readsThisYear: aggregates.readsYtd,
      readsThisYearSuffix: aggregates.readsYtdSuffix,
      readsThisMonth: aggregates.readsMonth,
      readsThisMonthSuffix: aggregates.readsMonthSuffix,
      listensThisYear: aggregates.listensYtd,
      listensThisYearSuffix: aggregates.listensYtdSuffix,
      listensThisMonth: aggregates.listensMonth,
      listensThisMonthSuffix: aggregates.listensMonthSuffix,
      mediaSpotlight: support.content.wellnessSpotlight,
    },
    achievements: {
      experiencePointsToDate: aggregates.totalXpYtd,
      experiencePointsToDateSuffix: aggregates.totalXpYtdSuffix,
      experiencePointsThisMonth: aggregates.totalXpMonth,
      experiencePointsThisMonthSuffix: aggregates.totalXpMonthSuffix,
      achievementsToDate: achievementsToDate.value,
      achievementsToDateSuffix: achievementsToDate.suffix,
      achievementsThisMonth: achievementsThisMonth.value,
      achievementsThisMonthSuffix: achievementsThisMonth.suffix,
      featuredChallengeJoinPercent: aggregates.featuredChallengeJoinPercent,
      featuredChallengeJoinPercentSuffix: aggregates.featuredChallengeJoinPercentSuffix,
      featuredChallengeJoinVisible,
      weeklyActivityGoalsPercent: aggregates.weeklyGoalReachedPercent,
      weeklyActivityGoalsPercentSuffix: aggregates.weeklyGoalReachedPercentSuffix,
      weeklyActivityGoalsTotal: aggregates.weeklyGoalReachedCount,
      weeklyActivityGoalsTotalSuffix: aggregates.weeklyGoalReachedCountSuffix,
      dailyStepsGoalsPercent: aggregates.stepsTargetMetPercent,
      dailyStepsGoalsPercentSuffix: aggregates.stepsTargetMetPercentSuffix,
      dailyStepsGoalsTotal: aggregates.stepsTargetMetCount,
      dailyStepsGoalsTotalSuffix: aggregates.stepsTargetMetCountSuffix,
      badges: support.badges ?? [],
    },
    leaderboards: {
      featured: support.leaderboards.featured,
      activityGroups: support.leaderboards.activityGroups,
    },
    exercise: {
      stepsThisYear: aggregates.stepsYtd,
      stepsThisYearSuffix: aggregates.stepsYtdSuffix,
      stepsThisMonth: aggregates.stepsMonth,
      stepsThisMonthSuffix: aggregates.stepsMonthSuffix,
      aroundTheEarthPercent: aggregates.aroundTheEarthPercent,
      aroundTheEarthPercentSuffix: aggregates.aroundTheEarthPercentSuffix,
      activeMinutesToDate: aggregates.totalWorkoutMinutesYtd,
      activeMinutesToDateSuffix: aggregates.totalWorkoutMinutesYtdSuffix,
      activeMinutesThisMonth: aggregates.totalWorkoutMinutesMonth,
      activeMinutesThisMonthSuffix: aggregates.totalWorkoutMinutesMonthSuffix,
      daysOfMovement: aggregates.daysOfMovement,
      daysOfMovementSuffix: aggregates.daysOfMovementSuffix,
      workoutsToDate: aggregates.totalWorkoutsYtd,
      workoutsToDateSuffix: aggregates.totalWorkoutsYtdSuffix,
      workoutsThisMonth: aggregates.totalWorkoutsMonth,
      workoutsThisMonthSuffix: aggregates.totalWorkoutsMonthSuffix,
      caloriesBurntToDate: aggregates.totalCaloriesYtd,
      caloriesBurntToDateSuffix: aggregates.totalCaloriesYtdSuffix,
      caloriesBurntThisMonth: aggregates.totalCaloriesMonth,
      caloriesBurntThisMonthSuffix: aggregates.totalCaloriesMonthSuffix,
      caloriesBurntFoodEquivalentToDate: aggregates.caloriesBurntBurgersYtd,
      caloriesBurntFoodEquivalentToDateSuffix: aggregates.caloriesBurntBurgersYtdSuffix,
      caloriesBurntFoodEquivalentThisMonth: aggregates.caloriesBurntBurgersMonth,
      caloriesBurntFoodEquivalentThisMonthSuffix: aggregates.caloriesBurntBurgersMonthSuffix,
      caloriesConsumedToDate: 0,
      caloriesConsumedToDateSuffix: '',
      caloriesConsumedBurgerEquivalentToDate: 0,
      caloriesConsumedBurgerEquivalentToDateSuffix: '',
      caloriesConsumedThisMonth: 0,
      caloriesConsumedThisMonthSuffix: '',
      caloriesConsumedBurgerEquivalentThisMonth: 0,
      caloriesConsumedBurgerEquivalentThisMonthSuffix: '',
      workoutsByType: {
        strengthThisMonth: aggregates.strengthMonth,
        strengthThisMonthSuffix: aggregates.strengthMonthSuffix,
        cardioThisMonth: aggregates.cardioMonth,
        cardioThisMonthSuffix: aggregates.cardioMonthSuffix,
        mobilityThisMonth: aggregates.mobilityMonth,
        mobilityThisMonthSuffix: aggregates.mobilityMonthSuffix,
        donutStrengthPct: aggregates.donutStrengthPct,
        donutCardioPct: aggregates.donutCardioPct,
        donutMobilityPct: aggregates.donutMobilityPct,
      },
      workoutsByTypeTitle: meta.monthLabel,
    },
    nutrition: {
      popularMealsMedia: support.meals,
      popularBlogMedia: support.content.nutritionBlog ?? emptyMedia(),
      popularPodcastMedia: support.content.nutritionPodcast ?? emptyMedia(),
    },
  };
};
